import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { AgGridMultiSelectTracker } from '../../Compliance/AgGrid/MultiSelectTracker';
import { DisplayName } from '../searchOperators.service';
import * as _ from 'lodash';
import { ColDef, ColumnState, FilterModel, GridApi, GridState } from 'ag-grid-community';

type Omit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>;

export interface AdvancedSearchRouteParams {
    searchId?: number;
    searchType?: string;
    filters?: any;
}

export interface AdvancedSearchResults extends Omit<Core.AdvancedSearchResult, 'dataTable'> {
    payloadCriteria: AdvancedSearchCriteria;
    dataTable: any[];
}

export interface SearchOutputColumns {
    [key: string]: any;
}

export interface SearchOr extends Omit<Core.AdvancedSearchFilterElement, 'operator'> {
    operator: DisplayName;
}

export interface SearchFilters extends Omit<Core.AdvancedSearchFilter, 'or'>, Core.AdvancedSearchField {
    categoryID: number;
    or: SearchOr[];
}

export interface AdvancedSearchCriteria extends Omit<Core.AdvancedSearchWidgetDrilldownRequest, 'outputColumns' | 'filters'> {
    categoryId?: number;
    defaultDisplay?: number;
    filters: SearchFilters[];
    groupByBatches: boolean;
    outputColumns: SearchOutputColumns[];
    outputInt?: number;
    rowVersion?: number;
    savedSearchID?: string;
}

export interface SavedSearchModel extends Omit<Core.SavedSearch, 'rowVersion'>, Omit<Core.SavedSearchDTO, 'searchCriteria' | 'rowVersion'> {
    searchCriteria: AdvancedSearchCriteria;
    type: number;
    rowVersion: number;
}

export interface RevokeShareModel extends Omit<Core.SavedSearchInputPatchDTO, 'searchCriteria' | 'rowVersion'> {
    searchCriteria: AdvancedSearchCriteria;
    rowVersion: number;
}

export interface SearchGridState {
    columnState: ColumnState[];
    filterState: FilterModel;
    selectedRows: Compliance.SelectedRowsModel;
}

@Injectable({
    providedIn: 'root'
})
export class AdvancedSearchPersistenceService {

    routeParams: AdvancedSearchRouteParams = {};
    results: AdvancedSearchResults;
    selectedResult = {};
    sort = null;
    searchType = null;
    showPageTitle: boolean = true;
    fromWidget: boolean = false;
    hiddenColumns = [];
    reloadingSearch: boolean = false;
    columnsExpanded: boolean = false;

    private _updateAdvancedSearchSubject: Subject<any> = new Subject();
    private _deleteAdvancedSearchSubject: Subject<any> = new Subject();
    private _advancedSearchSubject: Subject<SavedSearchModel> = new Subject();
    private _isSearchingSubject: Subject<boolean> = new Subject();
    private _showCriteriaSubject: BehaviorSubject<boolean> = new BehaviorSubject(true);

    private _updateAdvancedSearch$: Observable<any> = this._updateAdvancedSearchSubject.asObservable();
    private _deleteAdvancedSearch$: Observable<any> = this._deleteAdvancedSearchSubject.asObservable();
    private _advancedSearch$: Observable<SavedSearchModel> = this._advancedSearchSubject.asObservable();
    private _isSearching$: Observable<boolean> = this._isSearchingSubject.asObservable();
    private _showCriteria$: Observable<boolean> = this._showCriteriaSubject.asObservable();

    private _search: SavedSearchModel;
    private _scrollLocationIndex: number;
    private _loadedSearchCriteriaCache: AdvancedSearchCriteria;
    private _gridState: SearchGridState;

    get updateAdvancedSearch$() {
        return this._updateAdvancedSearch$;
    }

    get deleteAdvancedSearch$() {
        return this._deleteAdvancedSearch$;
    }

    get advancedSearch$() {
        return this._advancedSearch$;
    }

    get isSearching$() {
        return this._isSearching$;
    }

    get showCriteria$() {
        return this._showCriteria$;
    }

    set search(search: SavedSearchModel) {
        this._search = search;
    }

    get search(): SavedSearchModel {
        return this._search;
    }

    set scrollLocationIndex(index: number) {
        this._scrollLocationIndex = index;
    }

    get scrollLocationIndex(): number {
        return this._scrollLocationIndex;
    }

    set loadedSearchCriteriaCache(cache: AdvancedSearchCriteria) {
        this._loadedSearchCriteriaCache = cache;
    }

    get gridState(): SearchGridState {
        return this._gridState;
    }

    setGridState(columnState: ColumnState[], filterState: FilterModel, selectedRows: Compliance.SelectedRowsModel): void {
        this._gridState = { columnState, filterState, selectedRows };
    }

    clearGridState(): void {
        this._gridState = null;
    }

    updateAdvancedSearch(): void {
        this._updateAdvancedSearchSubject.next(undefined);
    }

    deleteAdvancedSearch(savedSearch: Core.AdvancedSearchList): void {
        this._deleteAdvancedSearchSubject.next(savedSearch);
    }

    setIsSearching(searching: boolean): void {
        this._isSearchingSubject.next(searching);
    }

    showCriteria(show: boolean): void {
        this._showCriteriaSubject.next(show);
    }

    clearSearchData(): void {
        this.routeParams = {};
        this.search = null;
        this.results = null;
        this.selectedResult = {};
        this.searchType = null;
        this.showPageTitle = true;
        this.fromWidget = false;
        this.hiddenColumns = [];
        this.reloadingSearch = false;
        this.columnsExpanded = false;
        this._loadedSearchCriteriaCache = null;
        this.setIsSearching(false);
        this.showCriteria(true);
    }

    setSearchCriteria(searchCriteria: AdvancedSearchCriteria) {
        if (!this.search) {
            this.search = {} as SavedSearchModel;
        }
        this.search['searchCriteria'] = searchCriteria;
        this._advancedSearchSubject.next(this.search);
    }

    resetSearchCriteria(): void {
        if (this._loadedSearchCriteriaCache) {
            this.setSearchCriteria(_.cloneDeep(this._loadedSearchCriteriaCache));
        }
    }

    applyGridState(gridApi: GridApi, gridTracker: AgGridMultiSelectTracker): void {
        if (!this.fromWidget && this.gridState) {
            // Set sort model
            if (this.gridState.columnState?.length) {
                gridApi.applyColumnState({ state: this.gridState.columnState || [] });
            }

            // Set filter model
            if (this.gridState.filterState) {
                gridApi.setFilterModel(this.gridState.filterState);
                const colDefs = gridApi.getColumnDefs();
                colDefs.forEach(x => (x as ColDef).floatingFilter = true);
                gridApi.updateGridOptions({ columnDefs: colDefs });

                gridApi.refreshHeader();
            }

            // Set selected rows
            if (this.gridState.selectedRows) {
                gridTracker.setSelectedRowsModel(this.gridState.selectedRows);
            }

            this.clearGridState();
        }
    }
}
